import React, { useEffect, useState } from "react";
import {
  Box, Text, VStack, HStack, Button, Icon, Divider, Link as CLink, Spinner, Center,
} from "@chakra-ui/react";
import { FiMail, FiPhone, FiMapPin, FiClock, FiMessageCircle } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { PageHeader } from "./portalUi";
import { COLORS } from "../styles/colors";
import { getCertStatus } from "../utils/formatHelpers";
import { supabase } from "../utils/supabaseClient";
import { getOperatingHoursRows } from "../utils/operatingHours";

const pickFeaturedCert = (certifications = []) =>
  [...certifications].sort((a, b) => {
    const aStatus = getCertStatus(a).status;
    const bStatus = getCertStatus(b).status;
    const rank = { ongoing: 0, upcoming: 1, completed: 2, unknown: 3 };
    return (rank[aStatus] ?? 3) - (rank[bStatus] ?? 3);
  })[0] || null;

const buildEnquiryContext = (studentFullName, certifications) => {
  const featuredCert = pickFeaturedCert(certifications);
  const name = (studentFullName && String(studentFullName).trim()) || "Student";
  const courseName =
    featuredCert?.full_certification?.name ||
    featuredCert?.run_name ||
    null;
  const intake = featuredCert?.intake;
  const batchLabel =
    courseName && intake != null && String(intake).trim() !== ""
      ? ` - (Batch ${intake})`
      : "";

  const message = courseName
    ? `Hi Admin, I am ${name}, I am taking the ${courseName}${batchLabel}. I have the following questions:\n1. `
    : `Hi Admin, I am ${name}. I have the following questions:\n1. `;
  const subject = courseName
    ? `LMS Enquiry on ${courseName}${batchLabel}.`
    : "LMS Enquiry";

  return { message, subject };
};

const buildStaffEnquiryMessage = (staffName, studentFullName, certifications) => {
  const featuredCert = pickFeaturedCert(certifications);
  const studentName =
    (studentFullName && String(studentFullName).trim()) || "a student";
  const courseName =
    featuredCert?.full_certification?.name ||
    featuredCert?.run_name ||
    null;
  const intake = featuredCert?.intake;
  const batchLabel =
    courseName && intake != null && String(intake).trim() !== ""
      ? ` (Batch ${intake})`
      : "";
  const greeting = staffName ? `Hello ${staffName}` : "Hello";

  if (courseName) {
    return `${greeting}, I am ${studentName} from the ${courseName}${batchLabel} course. I have a question regarding...`;
  }
  return `${greeting}, I am ${studentName}. I have a question regarding...`;
};

const getMailtoUrl = (email, subject, body) =>
  `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const getWhatsAppUrl = (phone, message) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const normalized =
    digits.length === 8 && /^[89]/.test(digits)
      ? `65${digits}`
      : digits.startsWith("65")
        ? digits
        : digits.length >= 8
          ? `65${digits.slice(-8)}`
          : digits;
  if (normalized.length < 8) return null;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
};

const mapOrgToContact = (org) => {
  if (!org) return null;
  const lms = org.lms_contact && typeof org.lms_contact === "object"
    ? org.lms_contact
    : {};
  const phone = lms.phone || org.phone || null;
  const staff = Array.isArray(lms.staff)
    ? lms.staff
        .filter((entry) => entry && entry.is_active !== false)
        .map((entry) => ({
          id: entry.id || `${entry.name}-${entry.phone}`,
          name: entry.name || "",
          role: entry.role || "",
          phone: entry.phone || "",
          responsibilities: Array.isArray(entry.responsibilities)
            ? entry.responsibilities.filter(Boolean)
            : [],
          note: entry.note || null,
          sort_order: typeof entry.sort_order === "number" ? entry.sort_order : 0,
        }))
        .sort((a, b) => a.sort_order - b.sort_order)
    : [];
  // Prefer structured operating_hours from TMS; fall back to legacy lms_contact.hours string.
  const hoursRows = getOperatingHoursRows(org.operating_hours);
  const hoursLegacy = !hoursRows && lms.hours ? String(lms.hours) : null;
  return {
    collegeName: org.name || null,
    email: org.email || null,
    address: org.address || null,
    phone,
    whatsapp: lms.whatsapp || phone,
    introMessage: lms.intro_message || null,
    staff,
    hoursRows,
    hoursLegacy,
  };
};

export const ContactTab = ({ certifications = [], studentFullName = "" }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from("organization")
          .select("name, email, address, phone, lms_contact, operating_hours")
          .eq("status", "active")
          .limit(1)
          .maybeSingle();

        if (fetchError) throw fetchError;
        const mapped = mapOrgToContact(data);
        if (!mapped) throw new Error("No organization contact found");
        if (!cancelled) setContact(mapped);
      } catch (err) {
        console.error("Error loading organization contact:", err);
        if (!cancelled) {
          setContact(null);
          setError("Could not load contact details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const { message, subject } = buildEnquiryContext(studentFullName, certifications);
  const mailtoUrl = contact?.email
    ? getMailtoUrl(contact.email, subject, message)
    : null;
  const whatsappUrl = contact?.whatsapp
    ? getWhatsAppUrl(contact.whatsapp, message)
    : null;
  const telHref = contact?.phone
    ? `tel:${String(contact.phone).replace(/\s/g, "")}`
    : null;

  return (
    <VStack spacing={{ base: 2.5, md: 3 }} align="stretch">
      <PageHeader
        icon={FiMessageCircle}
        iconColor={COLORS.shadowBlue}
        title="Contact Us"
      />

      {loading && (
        <Center py={8}>
          <Spinner size="md" color="purple.400" />
        </Center>
      )}

      {!loading && error && (
        <Box
          p={3}
          borderRadius="xl"
          border="1px solid"
          borderColor="red.100"
          bg="red.50"
        >
          <Text fontSize="sm" color="red.600">{error}</Text>
        </Box>
      )}

      {!loading && !error && contact && (
        <Box
          p={{ base: 3, md: 4 }}
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          {contact.collegeName && (
            <Text
              fontWeight="700"
              color={COLORS.maastrichtBlue}
              mb={{ base: 2, md: 3 }}
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="short"
            >
              {contact.collegeName}
            </Text>
          )}
          <VStack
            align="stretch"
            spacing={{ base: 2.5, md: 3 }}
            divider={<Divider />}
            bg="gray.50"
            p={{ base: 2.5, md: 3 }}
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
          >
            {contact.email && (
              <HStack spacing={3} align="flex-start">
                <Icon as={FiMail} color={COLORS.shadowBlue} mt={0.5} boxSize={5} />
                <Box minW={0}>
                  <Text fontSize="2xs" color="gray.500" fontWeight="600">Email</Text>
                  <CLink
                    href={mailtoUrl}
                    color="blue.600"
                    fontSize={{ base: "xs", md: "sm" }}
                    wordBreak="break-all"
                  >
                    {contact.email}
                  </CLink>
                </Box>
              </HStack>
            )}
            {contact.phone && (
              <HStack spacing={3} align="flex-start">
                <Icon as={FiPhone} color={COLORS.shadowBlue} mt={0.5} boxSize={5} />
                <Box>
                  <Text fontSize="2xs" color="gray.500" fontWeight="600">Phone</Text>
                  <CLink
                    href={telHref}
                    color="blue.600"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {contact.phone}
                  </CLink>
                </Box>
              </HStack>
            )}
            {contact.whatsapp && (
              <HStack spacing={3} align="flex-start">
                <Icon as={FiMessageCircle} color={COLORS.shinyShamrock} mt={0.5} boxSize={5} />
                <Box>
                  <Text fontSize="2xs" color="gray.500" fontWeight="600">WhatsApp</Text>
                  {whatsappUrl ? (
                    <CLink
                      href={whatsappUrl}
                      isExternal
                      color="blue.600"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      {contact.whatsapp}
                    </CLink>
                  ) : (
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700">
                      {contact.whatsapp}
                    </Text>
                  )}
                </Box>
              </HStack>
            )}
            {(contact.hoursRows || contact.hoursLegacy) && (
              <HStack spacing={3} align="flex-start">
                <Icon as={FiClock} color={COLORS.shadowBlue} mt={0.5} boxSize={5} />
                <Box minW={0} flex={1}>
                  <Text fontSize="2xs" color="gray.500" fontWeight="600" mb={1.5}>
                    Office hours
                  </Text>
                  {contact.hoursRows ? (
                    <VStack align="stretch" spacing={1.5}>
                      {contact.hoursRows.map((row) => (
                        <HStack key={row.label} align="center" spacing={2}>
                          <Text
                            fontSize={{ base: "xs", md: "sm" }}
                            color="gray.700"
                            fontWeight="500"
                            minW="4.75rem"
                            flexShrink={0}
                          >
                            {row.label}
                          </Text>
                          <Box
                            as="span"
                            px={2.5}
                            py={0.5}
                            borderRadius="full"
                            border="1px solid"
                            borderColor="gray.200"
                            bg={row.isClosed ? "gray.100" : "white"}
                            color={row.isClosed ? "gray.500" : "gray.700"}
                            fontSize={{ base: "2xs", md: "xs" }}
                            fontWeight="500"
                            lineHeight="short"
                            whiteSpace="nowrap"
                          >
                            {row.time}
                          </Box>
                        </HStack>
                      ))}
                    </VStack>
                  ) : (
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      color="gray.700"
                      whiteSpace="pre-line"
                    >
                      {contact.hoursLegacy}
                    </Text>
                  )}
                </Box>
              </HStack>
            )}
            {contact.address && (
              <HStack spacing={3} align="flex-start">
                <Icon as={FiMapPin} color={COLORS.shadowBlue} mt={0.5} boxSize={5} />
                <Box>
                  <Text fontSize="2xs" color="gray.500" fontWeight="600">Address</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700">{contact.address}</Text>
                </Box>
              </HStack>
            )}
          </VStack>

          {mailtoUrl && (
            <Button
              mt={{ base: 3, md: 4 }}
              bg={COLORS.mandarin}
              color="white"
              _hover={{ bg: "#e06a38" }}
              leftIcon={<FiMail />}
              as="a"
              href={mailtoUrl}
              size={{ base: "sm", md: "md" }}
              w={{ base: "100%", sm: "auto" }}
              borderRadius="xl"
            >
              Email support
            </Button>
          )}
        </Box>
      )}

      {!loading && !error && contact?.staff?.length > 0 && (
        <Box
          p={{ base: 3, md: 4 }}
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          {contact.introMessage && (
            <Text
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.600"
              mb={{ base: 3, md: 4 }}
              lineHeight="tall"
            >
              {contact.introMessage}
            </Text>
          )}

          <VStack align="stretch" spacing={{ base: 3, md: 4 }}>
            {contact.staff.map((member) => {
              const memberTelHref = member.phone
                ? `tel:${String(member.phone).replace(/\s/g, "")}`
                : null;
              const staffMessage = buildStaffEnquiryMessage(
                member.name,
                studentFullName,
                certifications,
              );
              const memberWhatsappUrl = member.phone
                ? getWhatsAppUrl(member.phone, staffMessage)
                : null;

              return (
                <Box
                  key={member.id}
                  p={{ base: 3, md: 3.5 }}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  bg="gray.50"
                >
                  <Text
                    fontWeight="700"
                    color={COLORS.maastrichtBlue}
                    fontSize={{ base: "sm", md: "md" }}
                    lineHeight="short"
                  >
                    {member.name}
                  </Text>
                  {member.role && (
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      color="gray.600"
                      mt={0.5}
                    >
                      {member.role}
                    </Text>
                  )}
                  {member.phone && (
                    <HStack spacing={2} mt={2} align="center">
                      <Icon as={FiPhone} color={COLORS.shadowBlue} boxSize={4} />
                      <CLink
                        href={memberTelHref}
                        color="blue.600"
                        fontSize={{ base: "xs", md: "sm" }}
                        fontWeight="600"
                      >
                        {member.phone}
                      </CLink>
                    </HStack>
                  )}
                  {member.responsibilities.length > 0 && (
                    <Box as="ul" mt={3} pl={4} color="gray.700">
                      {member.responsibilities.map((line) => (
                        <Box
                          as="li"
                          key={`${member.id}-${line}`}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          {line}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {member.note && (
                    <Text
                      mt={3}
                      fontSize={{ base: "2xs", md: "xs" }}
                      color="orange.700"
                      bg="orange.50"
                      border="1px solid"
                      borderColor="orange.100"
                      borderRadius="lg"
                      px={3}
                      py={2}
                    >
                      {member.note}
                    </Text>
                  )}
                  {memberWhatsappUrl && (
                    <Button
                      as="a"
                      href={memberWhatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      mt={3}
                      size="sm"
                      borderRadius="full"
                      leftIcon={<Icon as={FaWhatsapp} boxSize={4} />}
                      bg={COLORS.shinyShamrock}
                      color="white"
                      fontWeight="600"
                      _hover={{ opacity: 0.9 }}
                      w={{ base: "100%", sm: "auto" }}
                    >
                      WhatsApp
                    </Button>
                  )}
                </Box>
              );
            })}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};
